import _extends from "@babel/runtime/helpers/esm/extends";
import hash from "object-hash";
import { from, iif, merge, of, Subject, throwError } from "rxjs";
import { catchError, concatMap, delay, exhaustMap, filter, map, mergeMap, retryWhen, shareReplay, switchMap, tap } from "rxjs/operators";
import { ModuleUtils } from "../exports";

const generateUniqSerial = () => {
  return "xxxx-xxxx-xxx-xxxx".replace(/[x]/g, function (c) {
    var r = Math.random() * 16 | 0,
        v = c === "x" ? r : r & 0x3 | 0x8;
    return v.toString(16);
  });
};

export class Execution2 {
  get state$() {
    const state$ = this._state$.asObservable().pipe(shareReplay());

    return state$;
  }

  constructor(params, config) {
    this.params = params;
    this.config = config;
    this.id = void 0;
    this.status = void 0;
    this.data = void 0;
    this.error = void 0;
    this.hashedParams = void 0;
    this._state$ = new Subject();
    const conf = config || {
      cache: false,
      getId: () => generateUniqSerial()
    };
    this.hashedParams = conf.cache ? hash(params) : undefined;
    this.id = conf.getId ? conf.getId(params) : generateUniqSerial();
  }

  get state() {
    return this;
  }

  retrying() {
    this.status = "RETRYING";

    this._state$.next(this);

    this.status = "PROCESSING";

    this._state$.next(this);

    return this.state;
  }

  wait() {
    this.status = "WAITING";

    this._state$.next(this);

    return this.state;
  }

  processing() {
    this.status = "PROCESSING";

    this._state$.next(this);

    return this.state;
  }

  succeed(data) {
    this.data = data;
    this.status = "SUCCESS";

    this._state$.next(this);

    return this.state;
  }

  failed(error) {
    this.error = error;
    this.status = "FAILED";

    this._state$.next(this);

    return this.state;
  }

  cancelled() {
    this.status = "CANCELLED";

    this._state$.next(this);

    return this.state;
  }

  listen(callback) {
    this.state$.subscribe(callback);
  }

}
export const getTypeOperation2 = processingType => {
  switch (processingType) {
    case "CONCAT":
      return concatMap;

    case "EXHAUST":
      return exhaustMap;

    case "MERGE":
      return mergeMap;

    case "SWITCH":
      return switchMap;

    default:
      return switchMap;
  }
};
export class RxExecutor2 {
  get params() {
    return this._params;
  }

  get error() {
    return this._error;
  }

  get status() {
    return this._status;
  }

  get data() {
    return this._data;
  }

  get isLoading() {
    return this._status === "PROCESSING";
  }

  get isProcessing() {
    return this._status === "PROCESSING";
  }

  get isSuccess() {
    return this._status === "SUCCESS";
  }

  get isError() {
    return this._status === "FAILED";
  }

  retry() {
    return this.execute(this._params);
  }

  constructor(executeFn$, config) {
    var _tempConfig$concurren;

    this.executeFn$ = executeFn$;
    this.config = config;
    this._status = void 0;
    this._data = void 0;
    this._params = void 0;
    this._error = void 0;
    this._internalExecution$ = new Subject();
    this.executionType = void 0;
    this.operationType = void 0;
    this.processorCapacity = 1;
    this.enableCache = false;
    this.concurrentDetailed = false;
    this._state$ = void 0;
    this.cachedData = {};
    this.cachedExecution = {};
    this._execution = void 0;
    this.processingExecutions = [];
    this.failedExecutions = [];
    this.executionType = config ? config.concurrent ? "CONCURRENT" : "SEQUENTIAL" : "SEQUENTIAL";
    const tempConfig = this.config || {
      sequential: "SWITCH",
      concurrent: {
        type: "SWITCH",
        mergeCapacity: 1,
        detailed: true
      },
      cache: false
    };
    this.concurrentDetailed = !!((_tempConfig$concurren = tempConfig.concurrent) != null && _tempConfig$concurren.detailed);
    const {
      concurrent,
      sequential
    } = tempConfig;
    this.operationType = sequential || concurrent && concurrent.type || "SWITCH";

    if (this.operationType === "MERGE" && tempConfig.concurrent.mergeCapacity > 1) {
      this.processorCapacity = tempConfig.concurrent.mergeCapacity;
    }

    this.enableCache = !!tempConfig.cache;
    this.execute = this.execute.bind(this);
    this.close = this.close.bind(this);
    this.getExecution = this.getExecution.bind(this);
    this._state$ = this.getState$();
    this.config = config || {};
  }

  get state$() {
    return this._state$;
  }

  close() {}

  static create(executeFn$, config) {
    return new RxExecutor2(executeFn$, config);
  }

  getExecution(id) {
    return this._execution;
  }

  execute(params, config) {
    const execution = this._execute(params, config);

    this._execution = execution;

    this._internalExecution$.next(execution);

    return execution;
  }

  _execute(params, config) {
    var _this$config, _this$config2;

    const context = (config ? config.context : {}) || {};

    const mergedContext = _extends({}, this.config && this.config.context || {}, context);

    if (this.executionType === "SEQUENTIAL" && this.operationType === "EXHAUST") {
      const currentxecution = this.processingExecutions[0];

      if (currentxecution) {
        return currentxecution;
      }
    }

    const execution = new Execution2(params, {
      executeFn$: config == null ? void 0 : config.executeFn$,
      cache: (_this$config = this.config) == null ? void 0 : _this$config.cache,
      getId: (_this$config2 = this.config) == null ? void 0 : _this$config2.getId,
      ignoreCache: config == null ? void 0 : config.ignoreCache
    });
    const ignoreCache = (config == null ? void 0 : config.ignoreCache) || false;
    const enableCache = this.enableCache && !ignoreCache;

    if (enableCache) {
      const key = hash(params);
      this.cachedExecution[key] = execution;
    }

    execution.state$.subscribe(exec => {
      if (this.executionType === "SEQUENTIAL") {
        if (this.operationType === "EXHAUST" || this.operationType === "SWITCH") {
          this._params = exec.params;
          this._status = exec.status;
          if (exec.status === "SUCCESS") this._data = exec.data;
          if (exec.status === "FAILED") this._error = exec.error;
        }
      }

      if (exec.status === "RETRYING") {
        config && config.onRetry && config.onRetry(exec.error, exec.params);
        this.config && this.config.onRetry && this.config.onRetry(exec.error, exec.params);
      }

      if (exec.status === "PROCESSING") {
        config && config.onProcessing && config.onProcessing(exec.params, mergedContext);
        this.config && this.config.onProcessing && this.config.onProcessing(exec.params, mergedContext);
      }

      if (exec.status === "SUCCESS") {
        config && config.onSuccess && config.onSuccess(exec.data, exec.params, mergedContext);
        this.config && this.config.onSuccess && this.config.onSuccess(exec.data, exec.params, mergedContext);
      }

      if (exec.status === "FAILED") {
        config && config.onError && config.onError(exec.error, exec.params, mergedContext);
        this.config && this.config.onError && this.config.onError(exec.error, exec.params, mergedContext);
      }

      if (exec.status === "WAITING") {
        config && config.onWait && config.onWait(exec.params, mergedContext);
        this.config && this.config.onWait && this.config.onWait(exec.params, mergedContext);
      }

      if (exec.status === "CANCELLED") {
        config && config.onCancel && config.onCancel(exec.params, mergedContext);
        this.config && this.config.onCancel && this.config.onCancel(exec.params, mergedContext);
      }
    });
    return execution;
  }

  invalidateCache(params) {
    const key = hash(params);
    const found = !!this.cachedData[key];
    delete this.cachedData[key];
    return found;
  }

  clearCache() {
    this.cachedData = {};
  }

  isFull() {
    return this.processingExecutions.length >= this.processorCapacity;
  }

  getState$() {
    const mergedExecutions$ = this.config && this.config.params$ ? merge(this._internalExecution$, this.config.params$.pipe(map(params => this._execute(params)))) : this._internalExecution$;
    const asyncOperation = getTypeOperation2(this.operationType);
    const waitingSubject = new Subject();
    const retrySubject = new Subject();
    const retryConfig = this.config && this.config.retry ? this.config.retry : {
      interval: 1000,
      maxRetryAttempts: 0
    };
    const maxRetryAttempts = retryConfig.maxRetryAttempts ? retryConfig.maxRetryAttempts : 0;
    const notRetryWhenStatus = retryConfig.notRetryWhenStatus ? retryConfig.notRetryWhenStatus : [];
    return merge(retrySubject.pipe(map(execution => {
      execution.retrying();
      return execution.state;
    })), waitingSubject.pipe(filter(() => {
      return this.operationType === "SWITCH" && this.executionType === "CONCURRENT";
    }), map(execution => {
      execution.wait();
      return execution.state;
    })), mergedExecutions$.pipe(tap(execution => {
      waitingSubject.next(execution);
    }), asyncOperation(execution => {
      var _execution$config, _execution$config2, _this$config3;

      const {
        params
      } = execution;
      const ignoreCache = ((_execution$config = execution.config) == null ? void 0 : _execution$config.ignoreCache) || false;
      const enableCache = this.enableCache && !ignoreCache;
      let cachedData = undefined;

      if (enableCache) {
        const key = hash(params);
        cachedData = this.cachedData[key];
      }

      const executeFn$ = ((_execution$config2 = execution.config) == null ? void 0 : _execution$config2.executeFn$) || this.executeFn$;
      const data$ = executeFn$ ? executeFn$(params) : of(params).pipe(map(p => p));
      const loadData$ = enableCache && cachedData !== undefined ? of(cachedData).pipe(delay(1)) : ModuleUtils.isObservable(data$) ? data$ : ModuleUtils.isPromise(data$) ? from(data$) : of(data$);
      const wrapData$ = (_this$config3 = this.config) != null && _this$config3.wrapData$ ? this.config.wrapData$(loadData$) : loadData$;
      return merge(of(execution).pipe(filter(() => {
        return this.operationType === "SWITCH" && this.executionType === "CONCURRENT" && this.isFull();
      }), map(() => {
        const execution = this.processingExecutions[0];
        execution.cancelled();
        return execution.state;
      })), of(execution).pipe(filter(() => {
        return this.operationType !== "SWITCH" || this.executionType !== "SEQUENTIAL" || !this.isFull();
      }), tap(execution => {
        this.processingExecutions.push(execution);
      }), map(execution => {
        execution.processing();
        return execution.state;
      })), wrapData$.pipe(
      /*********************************************************/
      retryWhen(errors => errors.pipe(tap(e => {// execution.addError(e);
      }), concatMap((e, i) => iif(() => {
        if (i < maxRetryAttempts) {
          const responseStatus = (e || {}).status || 0;
          const notRetry = !!notRetryWhenStatus.find(status => status === responseStatus);

          if (notRetry) {
            return true;
          }

          if (retryConfig.noRetryWhen && retryConfig.noRetryWhen(e)) {
            return true;
          }

          return false;
        }

        return true;
      }, throwError(() => e), of(e).pipe(tap(() => {
        retrySubject.next(execution);
      }), delay((retryConfig.typeInterval === "LINEAR" ? 1 : i) * retryConfig.interval)))))),
      /*********************************************************/
      map(resp => {
        execution.succeed(resp);
        return execution.state;
      }), tap(execution => {
        this.processingExecutions = this.processingExecutions.filter(e => e !== execution);

        if (this.enableCache) {
          this.cachedData[execution.hashedParams] = execution.data;
        }
      }, () => {
        this.processingExecutions = this.processingExecutions.filter(e => e !== execution);
      }), catchError(error => {
        execution.failed(error);
        return of(execution.state);
      })));
    }))).pipe(shareReplay());
  }

}